export type Hotel = {
  id: string;
  name: string;
  image: string;
  gallery: string[];
  area: string;
  shortDescription: string;
  dinnerStyle: string;
  cuisineOptions: string[];
  idealFor: string;
  priceLabel: string;
  reservationUrl: string;
  phone: string;
  featured: boolean;
  filters: string[];
};

export type Tour = {
  id: string;
  destination: string;
  title: string;
  image: string;
  duration: string;
  description: string;
  highlights: string[];
  tag: string;
  weatherBadge: string;
  difficultyBadge: string;
  filters: string[];
  itinerary: Array<{ day: string; activities: string[] }>;
};

export const birthdayConfig = {
  site: {
    title: 'Happy Birthday IQRA ZAIN 💖',
    description: 'A little world of memories, wishes and surprises made with love.',
  },

  birthdayPerson: {
    name: 'IQRA ZAIN',
    nickname: 'IQRA ZAIN',
   birthdayDate: '2026-08-03',
    age: '',
    heroMessage: 'Happy Birthday to the most beautiful person in my world',
    shortMessage:
      'Today is all about you, your smile and the beautiful memories that make our little world feel magical.',
  },

  sender: {
    name: 'ZAIN ALI',
    whatsappNumber: '923041783151',
  },

  theme: {
    primaryColor: '#ff4fa3',
    secondaryColor: '#a855f7',
    accentColor: '#ffd166',
    enableRainbowGlow: true,
    enableFlowerRain: true,
    enableHeartRain: true,
    enableSparkles: true,
  },

  audio: {
    youtubeVideoId: 'cYOB941gyXI',
    preferYouTubeTrack: true,
    backgroundTrack: '/audio/background-music.mp3',
    birthdayTrack: '/audio/birthday-song.mp3',
    noButtonTrack: '/audio/no-no-baby-no.mp3',
    yesButtonTrack: '/audio/yes-celebration.mp3',
    envelopeTrack: '/audio/envelope-open.mp3',
    backgroundVolume: 0.4,
    loopBackgroundMusic: true,
  },

  opening: {
    title: 'A Special Surprise Is Waiting For You',
    subtitle: 'Put on your headphones and tap to begin',
    buttonLabel: 'Open My Surprise 🎁',
  },

  copy: {
    hero: {
      eyebrow: 'A Special Day For A Special Person',
      titlePrefix: 'Happy Birthday,',
      openLetterButton: 'Open Your Letter',
      journeyButton: 'Begin Our Journey',
      birthdayToday: 'Today Is Your Day! 🎂',
    },
    letter: {
      eyebrow: 'A letter from my heart',
      description: 'Some feelings deserve more than a quick message. Open this slowly.',
      openButton: 'Open This Letter',
      miniLetters: [
        { id: 'wish', title: 'A Birthday Wish', messageKey: 'birthdayWish' },
        { id: 'dinner', title: 'Our Dinner Invitation', messageKey: 'dinnerInvitation' },
        { id: 'travel', title: 'A December Adventure', messageKey: 'travelInvitation' },
      ],
    },
    loveGame: {
      eyebrow: 'One very important question',
      question: 'Do You Love Me? 🥺💗',
      description: 'Choose carefully. One button is clearly more cooperative than the other.',
      yesButton: 'Yes, Of Course! 💖',
      acceptedTitle: 'I Knew It! You Just Made My Heart Very Happy 💕',
      acceptedText: 'Your answer has officially been accepted forever.',
      kissButton: 'Collect Your Birthday Kiss 💋',
      noLabels: ['No 🙈', 'Are You Sure? 🥺', 'Think Again 😭', 'Wrong Button 😼', 'Try The Pink One 💗', 'No Escape 😹'],
      funnyMessages: [
        'The button has entered survival mode.',
        'Mochi Cat respectfully rejects that answer.',
        'That option appears to be temporarily unavailable.',
        'Your finger is clearly looking for the pink button.',
        'Nice try. The heart has other plans.',
      ],
    },
    memories: {
      eyebrow: 'Six little windows into us',
      title: 'Our Memory Gallery',
      description: 'Tap a memory to see it full-screen. Replace every sample image with your own photograph in the public/images folder.',
      shuffleButton: 'Polaroid Shuffle',
    },
    reasons: {
      eyebrow: 'Eight reasons, among a thousand',
      title: 'Things I Love About You',
      description: 'The list could be endless. These are only a few of the reasons you are so special.',
    },
    dinner: {
      eyebrow: 'A celebration planned around you',
      title: 'Choose Your Perfect Birthday Dinner ✨',
      description: 'I planned a few beautiful options. Tonight, you are the one making the final choice.',
      summaryEyebrow: 'Live selection',
      summaryTitle: 'Your Birthday Dinner Plan',
      saveButton: 'Save My Dinner Choice',
      whatsappButton: 'Send My Choice on WhatsApp',
      disclaimer: 'This page prepares your preference only. It does not make or confirm a reservation.',
    },
    hotels: {
      eyebrow: 'Six configurable venue ideas',
      title: 'Featured Lahore Dinner Options',
      description: 'These are celebration ideas, not an objective ranking. Confirm availability, menus, décor rules and current prices directly with each venue.',
      filters: ['All', 'Romantic', 'Buffet', 'Quiet', 'Central Lahore', 'Gulberg', 'Johar Town', 'Traditional', 'Luxury'],
      availabilityNotice: 'Availability, menu and prices must be confirmed directly with the venue.',
    },
    tours: {
      eyebrow: 'A winter story waiting to happen',
      title: 'Where Should We Go This December? ❄️',
      description: 'Choose the adventure you would love to experience together. Every itinerary is editable from config.ts.',
      filters: ['All', 'Snow', 'Relaxing', 'Short trip', 'Adventure', 'City', 'Food', 'Mountains', 'Budget-friendly', 'Luxury'],
      summaryTitle: 'Our December Adventure',
      notice: 'Tour routes, flights, snowfall, weather and hotel availability can change. Confirm current conditions before booking.',
    },
    coupons: {
      eyebrow: 'Tiny gifts with unlimited meaning',
      title: 'Birthday Coupons & Surprise Options',
      description: 'Tap each coupon to unlock it. Your unlocked gifts stay saved on this device.',
    },
    final: {
      eyebrow: 'One final thing',
      title: 'Of All the Beautiful Moments in My Life,\nYou Are Still My Favourite.',
      description: 'May this birthday be the beginning of your happiest, calmest and most beautiful year yet.',
      wishButton: 'Make a Birthday Wish',
      wishMessage: 'May every beautiful wish in your heart come true.',
      replayButton: 'Replay the Surprise',
      photosButton: 'See Our Photos Again',
      choicesButton: 'Send Me Your Choices',
      loveButton: 'Yes, I Love You 💖',
    },
  },

  messages: {
    mainLetter: 'Private letter. Tap to unlock.',
    birthdayWish:
      'Private birthday wish. Tap to unlock.',
    dinnerInvitation:
      'Private dinner invitation. Tap to unlock.',
    travelInvitation:
      'Private travel invitation. Tap to unlock.',
  },

  memories: [
    {
      image: '/images/memory-1.jpg',
      title: 'My Favourite Smile',
      message: 'This smile can make even my most difficult day feel beautiful.',
      date: 'Our beautiful memory',
      sticker: '/stickers/mochi-heart.svg',
    },
    {
      image: '/images/memory-2.jpg',
      title: 'A Moment I Never Want to Forget',
      message: 'Some memories become part of your heart forever.',
      date: 'A moment worth keeping',
      sticker: '/stickers/mochi-shy.svg',
    },
    {
      image: '/images/memory-3.jpg',
      title: 'My Favourite Person',
      message: 'Out of everyone in this world, I would still choose you.',
      date: 'Always my choice',
      sticker: '/stickers/mochi-love.svg',
    },
    {
      image: '/images/memory-4.jpg',
      title: 'Our Little Adventures',
      message: 'Every place feels special when I experience it with you.',
      date: 'One adventure at a time',
      sticker: '/stickers/mochi-hug.svg',
    },
    {
      image: '/images/memory-5.jpg',
      title: 'You Make Life Brighter',
      message: 'Thank you for bringing warmth, colour and happiness into my life.',
      date: 'You are my sunshine',
      sticker: '/stickers/mochi-celebrate.svg',
    },
    {
      image: '/images/memory-6.jpg',
      title: 'More Memories Are Waiting',
      message: 'This is only one chapter. Our most beautiful moments are still ahead.',
      date: 'To be continued…',
      sticker: '/stickers/mochi-kiss.svg',
    },
  ],

  reasons: [
    { title: 'Your smile', text: 'It changes the whole atmosphere around you.', icon: '💗' },
    { title: 'Your kindness', text: 'You make people feel seen, safe and valued.', icon: '🌸' },
    { title: 'Your cute reactions', text: 'They can make an ordinary moment unforgettable.', icon: '🥺' },
    { title: 'Your support', text: 'You make hard days feel lighter and good days feel complete.', icon: '🤍' },
    { title: 'Your honesty', text: 'It gives our connection trust, clarity and strength.', icon: '✨' },
    { title: 'Your beautiful heart', text: 'It is one of the rarest and loveliest things about you.', icon: '💖' },
    { title: 'The peace I feel with you', text: 'Your presence feels like home.', icon: '🌙' },
    { title: 'The memories we create', text: 'Even the smallest ones become special.', icon: '📸' },
  ],

  dinner: {
    atmospheres: [
      'Romantic candlelight',
      'Rooftop dinner',
      'Luxury hotel dinner',
      'Traditional Lahore dinner',
      'Private decorated table',
      'Casual and cosy dinner',
    ],
    cuisines: ['Pakistani', 'Continental', 'Chinese', 'Japanese', 'Italian', 'Steak', 'Buffet', 'Surprise me'],
    decorations: [
      'Pink roses',
      'Red roses',
      'Fairy lights',
      'Candles',
      'Balloons',
      'Birthday cake',
      'Printed photos',
      'Minimal elegant setup',
    ],
    times: ['7:00 PM', '8:00 PM', '9:00 PM', 'Custom time'],
    extras: [
      'Birthday cake',
      'Flower bouquet',
      'Handwritten letter',
      'Surprise gift',
      'Photographer',
      'Pickup and drop',
      'Custom song request',
    ],
  },

  hotels: [
    {
      id: 'pc-lahore',
      name: 'Pearl-Continental Hotel Lahore',
      image: '/images/hotels/pc-lahore.jpg',
      gallery: ['/images/hotels/pc-lahore.jpg', '/images/hotels/hotel-detail-1.jpg'],
      area: 'Mall Road, Lahore',
      shortDescription: 'A polished city-hotel setting with multiple dining styles and a classic celebration atmosphere.',
      dinnerStyle: 'Luxury hotel dinner with formal or relaxed venue options.',
      cuisineOptions: ['Pakistani', 'Continental', 'Buffet'],
      idealFor: 'A classic luxury birthday evening',
      priceLabel: 'Contact venue for current pricing',
      reservationUrl: 'https://www.google.com/maps/search/?api=1&query=Pearl+Continental+Hotel+Lahore',
      phone: '',
      featured: true,
      filters: ['Luxury', 'Buffet', 'Central Lahore'],
    },
    {
      id: 'avari-lahore',
      name: 'Avari Hotel Lahore',
      image: '/images/hotels/avari-lahore.jpg',
      gallery: ['/images/hotels/avari-lahore.jpg', '/images/hotels/hotel-detail-2.jpg'],
      area: 'Mall Road, Lahore',
      shortDescription: 'A central Lahore option suited to an elegant dinner with a quieter, established hotel feel.',
      dinnerStyle: 'Elegant hotel dining and birthday celebration.',
      cuisineOptions: ['Pakistani', 'Continental', 'Japanese'],
      idealFor: 'A refined and central dinner plan',
      priceLabel: 'Contact venue for current pricing',
      reservationUrl: 'https://www.google.com/maps/search/?api=1&query=Avari+Hotel+Lahore',
      phone: '',
      featured: false,
      filters: ['Quiet', 'Luxury', 'Central Lahore'],
    },
    {
      id: 'nishat-gulberg',
      name: 'The Nishat Hotel Gulberg',
      image: '/images/hotels/nishat-gulberg.jpg',
      gallery: ['/images/hotels/nishat-gulberg.jpg', '/images/hotels/hotel-detail-3.jpg'],
      area: 'Gulberg, Lahore',
      shortDescription: 'A modern boutique-luxury atmosphere close to Lahore’s active dining and shopping district.',
      dinnerStyle: 'Modern luxury dinner with a romantic city feel.',
      cuisineOptions: ['Continental', 'Steak', 'Buffet'],
      idealFor: 'A modern romantic night in Gulberg',
      priceLabel: 'Contact venue for current pricing',
      reservationUrl: 'https://www.google.com/maps/search/?api=1&query=The+Nishat+Hotel+Gulberg+Lahore',
      phone: '',
      featured: true,
      filters: ['Romantic', 'Luxury', 'Gulberg'],
    },
    {
      id: 'nishat-johar-town',
      name: 'The Nishat Hotel Johar Town',
      image: '/images/hotels/nishat-johar-town.jpg',
      gallery: ['/images/hotels/nishat-johar-town.jpg', '/images/hotels/hotel-detail-4.jpg'],
      area: 'Johar Town, Lahore',
      shortDescription: 'A spacious contemporary option connected to a lively part of the city and suitable for a full celebration plan.',
      dinnerStyle: 'Contemporary hotel dinner with flexible celebration options.',
      cuisineOptions: ['Pakistani', 'Continental', 'Buffet'],
      idealFor: 'A comfortable Johar Town celebration',
      priceLabel: 'Contact venue for current pricing',
      reservationUrl: 'https://www.google.com/maps/search/?api=1&query=The+Nishat+Hotel+Johar+Town+Lahore',
      phone: '',
      featured: false,
      filters: ['Buffet', 'Luxury', 'Johar Town'],
    },
    {
      id: 'falettis',
      name: 'Faletti’s Hotel Lahore',
      image: '/images/hotels/falettis-lahore.jpg',
      gallery: ['/images/hotels/falettis-lahore.jpg', '/images/hotels/hotel-detail-5.jpg'],
      area: 'Egerton Road, Lahore',
      shortDescription: 'A heritage-inspired setting with old-Lahore character and a distinct, traditional celebration mood.',
      dinnerStyle: 'Heritage dinner with classic Lahore ambience.',
      cuisineOptions: ['Pakistani', 'Continental', 'Traditional'],
      idealFor: 'A heritage-style romantic dinner',
      priceLabel: 'Contact venue for current pricing',
      reservationUrl: 'https://www.google.com/maps/search/?api=1&query=Falettis+Hotel+Lahore',
      phone: '',
      featured: true,
      filters: ['Romantic', 'Traditional', 'Central Lahore'],
    },
    {
      id: 'luxus-grand',
      name: 'Luxus Grand Hotel Lahore',
      image: '/images/hotels/luxus-grand-lahore.jpg',
      gallery: ['/images/hotels/luxus-grand-lahore.jpg', '/images/hotels/hotel-detail-6.jpg'],
      area: 'Near Mall Road, Lahore',
      shortDescription: 'A contemporary hotel choice for a comfortable dinner, personalised décor and an easy central-city plan.',
      dinnerStyle: 'Contemporary luxury dinner with a cosy atmosphere.',
      cuisineOptions: ['Pakistani', 'Continental', 'Buffet'],
      idealFor: 'A cosy luxury celebration',
      priceLabel: 'Contact venue for current pricing',
      reservationUrl: 'https://www.google.com/maps/search/?api=1&query=Luxus+Grand+Hotel+Lahore',
      phone: '',
      featured: false,
      filters: ['Quiet', 'Luxury', 'Central Lahore'],
    },
  ] satisfies Hotel[],

  tours: [
    {
      id: 'murree',
      destination: 'Murree & Nathia Gali',
      title: 'Murree Snow Weekend',
      image: '/images/tours/murree-nathia-gali.jpg',
      duration: '2 Days / 1 Night',
      description: 'A cosy, short winter escape for warm drinks, hill views and slow moments together.',
      highlights: ['Winter views', 'Mall Road walk', 'Kashmir Point', 'Nathia Gali day visit', 'Hot chocolate', 'Cosy hotel evening', 'Couple photographs'],
      tag: 'Best for a short snowy escape',
      weatherBadge: 'Cold • Possible snow',
      difficultyBadge: 'Easy–Moderate',
      filters: ['Snow', 'Short trip', 'Relaxing', 'Budget-friendly'],
      itinerary: [
        { day: 'Day 1', activities: ['Departure for Murree', 'Hotel check-in', 'Mall Road and Kashmir Point', 'Cosy dinner and hot chocolate'] },
        { day: 'Day 2', activities: ['Breakfast', 'Nathia Gali day visit', 'Photo session', 'Return journey'] },
      ],
    },
    {
      id: 'karachi',
      destination: 'Karachi',
      title: 'Karachi Seaside Escape',
      image: '/images/tours/karachi-seaside.jpg',
      duration: '3 Days / 2 Nights',
      description: 'A lively December city break combining sea views, iconic food and golden-hour photographs.',
      highlights: ['Clifton seaside', 'Do Darya dinner', 'Burns Road food experience', 'Quaid-e-Azam Mausoleum', 'Sunset photography', 'Shopping', 'Comfortable December weather'],
      tag: 'Best for food, sea and city energy',
      weatherBadge: 'Mild • Breezy',
      difficultyBadge: 'Easy',
      filters: ['City', 'Food', 'Relaxing'],
      itinerary: [
        { day: 'Day 1', activities: ['Arrival and hotel check-in', 'Clifton seaside', 'Sunset photographs', 'Do Darya dinner'] },
        { day: 'Day 2', activities: ['Breakfast', 'Mazar-e-Quaid', 'Shopping', 'Burns Road food experience'] },
        { day: 'Day 3', activities: ['Relaxed breakfast', 'Final seaside stop', 'Souvenirs', 'Return journey'] },
      ],
    },
    {
      id: 'hunza',
      destination: 'Hunza',
      title: 'Hunza Winter Romance',
      image: '/images/tours/hunza-winter.jpg',
      duration: '5 Days / 4 Nights',
      description: 'A peaceful mountain retreat for quiet mornings, dramatic views and meaningful time together.',
      highlights: ['Karimabad', 'Baltit Fort surroundings', 'Altit village', 'Attabad Lake views', 'Mountain photography', 'Local food', 'Quiet winter atmosphere'],
      tag: 'Best for mountain scenery and peaceful moments',
      weatherBadge: 'Very cold • Winter roads',
      difficultyBadge: 'Moderate',
      filters: ['Mountains', 'Adventure', 'Snow', 'Luxury'],
      itinerary: [
        { day: 'Day 1', activities: ['Travel to Hunza', 'Hotel check-in', 'Rest and local dinner'] },
        { day: 'Day 2', activities: ['Karimabad walk', 'Baltit Fort surroundings', 'Sunset viewpoint'] },
        { day: 'Day 3', activities: ['Altit village', 'Local food', 'Couple photography'] },
        { day: 'Day 4', activities: ['Attabad Lake views', 'Scenic drive', 'Quiet hotel evening'] },
        { day: 'Day 5', activities: ['Breakfast', 'Souvenir stop', 'Return journey'] },
      ],
    },
    {
      id: 'swat',
      destination: 'Swat & Malam Jabba',
      title: 'Swat Snow Adventure',
      image: '/images/tours/swat-malam-jabba.jpg',
      duration: '3 Days / 2 Nights',
      description: 'An energetic winter trip with snow activities, resort time and beautiful mountain scenery.',
      highlights: ['Malam Jabba', 'Snow activities', 'Mountain views', 'Mingora', 'Local food', 'Resort stay'],
      tag: 'Best for snow activities',
      weatherBadge: 'Cold • Snow likely',
      difficultyBadge: 'Moderate',
      filters: ['Snow', 'Adventure', 'Mountains', 'Short trip'],
      itinerary: [
        { day: 'Day 1', activities: ['Departure for Swat', 'Mingora stop', 'Resort check-in', 'Dinner'] },
        { day: 'Day 2', activities: ['Breakfast', 'Malam Jabba', 'Snow activities', 'Mountain photo session'] },
        { day: 'Day 3', activities: ['Relaxed morning', 'Local food', 'Scenic return journey'] },
      ],
    },
    {
      id: 'skardu',
      destination: 'Skardu',
      title: 'Skardu Winter Dream',
      image: '/images/tours/skardu-winter.jpg',
      duration: '5 Days / 4 Nights',
      description: 'A dramatic winter landscape for travellers who want stillness, photography and unforgettable scenery.',
      highlights: ['Snow-covered landscapes', 'Upper Kachura surroundings', 'Shigar Valley', 'Cold desert views', 'Photography', 'Peaceful winter stay'],
      tag: 'Best for a dramatic winter experience',
      weatherBadge: 'Extreme cold • Check access',
      difficultyBadge: 'Challenging',
      filters: ['Snow', 'Mountains', 'Adventure', 'Luxury'],
      itinerary: [
        { day: 'Day 1', activities: ['Travel to Skardu', 'Hotel check-in', 'Warm dinner and rest'] },
        { day: 'Day 2', activities: ['Upper Kachura surroundings', 'Photography', 'Local dinner'] },
        { day: 'Day 3', activities: ['Shigar Valley', 'Village exploration', 'Quiet evening'] },
        { day: 'Day 4', activities: ['Cold desert viewpoints', 'Flexible weather-safe activity', 'Final-night dinner'] },
        { day: 'Day 5', activities: ['Breakfast', 'Check-out', 'Return journey'] },
      ],
    },
    {
      id: 'lahore-staycation',
      destination: 'Lahore',
      title: 'Royal Lahore Birthday Staycation',
      image: '/images/tours/lahore-staycation.jpg',
      duration: '2 Days / 1 Night',
      description: 'A relaxed birthday plan with luxury, heritage and no long-distance travel.',
      highlights: ['Luxury hotel stay', 'Birthday dinner', 'Old Lahore tour', 'Badshahi Mosque surroundings', 'Fort Road evening', 'Breakfast together', 'Relaxing spa or shopping time'],
      tag: 'Best without long-distance travel',
      weatherBadge: 'Cool • City winter',
      difficultyBadge: 'Easy',
      filters: ['Short trip', 'City', 'Food', 'Luxury', 'Relaxing'],
      itinerary: [
        { day: 'Day 1', activities: ['Hotel check-in', 'Old Lahore and Fort Road', 'Birthday dinner', 'Cake and surprise'] },
        { day: 'Day 2', activities: ['Breakfast together', 'Spa, shopping or café time', 'Late check-out', 'Return home'] },
      ],
    },
  ] satisfies Tour[],

  coupons: [
    { id: 'dinner', title: 'One Romantic Dinner', detail: 'A dinner planned around your favourite food and atmosphere.', sticker: '/stickers/mochi-love.svg' },
    { id: 'gift', title: 'One Surprise Gift', detail: 'A mystery present chosen especially for you.', sticker: '/stickers/mochi-celebrate.svg' },
    { id: 'drive', title: 'One Long Drive', detail: 'Music, snacks and no destination pressure.', sticker: '/stickers/mochi-heart.svg' },
    { id: 'movie', title: 'One Movie Night', detail: 'Your film, your snacks and the best seat beside me.', sticker: '/stickers/mochi-hug.svg' },
    { id: 'trip', title: 'One December Trip', detail: 'One beautiful destination selected together.', sticker: '/stickers/mochi-shy.svg' },
    { id: 'hugs', title: 'Unlimited Hugs', detail: 'Valid every day, with no expiry date.', sticker: '/stickers/mochi-hug.svg' },
    { id: 'kisses', title: 'Unlimited Birthday Kisses', detail: 'Redeemable immediately and repeatedly.', sticker: '/stickers/mochi-kiss.svg' },
    { id: 'wish', title: 'One Wish I Cannot Refuse', detail: 'Use wisely. This coupon has serious power.', sticker: '/stickers/mochi-heart.svg' },
  ],

  navigation: [
    { id: 'home', label: 'Home' },
    { id: 'letter', label: 'Letter' },
    { id: 'memories', label: 'Memories' },
    { id: 'dinner', label: 'Dinner' },
    { id: 'hotels', label: 'Hotels' },
    { id: 'trips', label: 'December Trips' },
    { id: 'surprise', label: 'Surprise' },
  ],
};

export type BirthdayConfig = typeof birthdayConfig;
